import { useLoaderState, dispatchEventToParentWindow, useConfig } from 'react-commons';
import { OrderCartService } from '../../../services';
import { useGlobalState } from '../../../state/persistence';
import PaymentTypeEnum from '../../../types/PaymentTypeEnum';
import { useGetIntegratedPartnerId, useGetSelectedLegalInsurance } from './index';
import { ReferenceLabelTypeEnum } from '../../../types';
const getLoginUrlWithTransitParams = (params: any) => {
  const loginUrl = new URL('/login/', location.origin);
  Object.keys(params).forEach((key) => params[key] && loginUrl.searchParams.append(key, params[key]));
  return loginUrl.toString();
};

const getState = (
  store: ReturnType<typeof useGlobalState>['appStore'],
  config: { partnerRedirectBaseUrl?: string },
): string | null => {
  if (config.partnerRedirectBaseUrl && store.partner?.slug === 'is24' && store.subscription) {
    const redirectUrl = `${config.partnerRedirectBaseUrl}/meinkonto/vorteile/nebenkosten-check/form`;
    return JSON.stringify({
      redirectUrl,
    });
  }

  return null;
};

const getCookie = (name: string) => {
  const cookies = document.cookie.split('; ').find((row) => row.startsWith(`${name}=`));

  return cookies ? cookies.split('=')[1] : null;
};

export default function useCreateOrderCart() {
  const integratedPartnerId = useGetIntegratedPartnerId();
  const legalInsurance = useGetSelectedLegalInsurance();
  const config = useConfig();
  const abTestCookieName = `order-flow-app-abtest-${config.APP.REACT_APP_ENV}`;
  const abTestVariant = getCookie(abTestCookieName);

  const { product, question, appStore } = useGlobalState();
  const { dispatchLoading } = useLoaderState();

  const transitUrlParams = {
    email: appStore.email,
    salutation: appStore.salutation,
    firstName: appStore.firstName,
    lastName: appStore.lastName,
    ref: appStore.ref,
  };

  const persistTransitParams = () => {
    try {
      localStorage.setItem('user-meta-data', JSON.stringify(transitUrlParams));
    } catch (e) {
      console.error('Failed to persist user meta data', e);
    }
  };

  const createOrderCart = (billingConsentTimestamp?: string, unionOptionsConsentTimestamp?: string): Promise<void> => {
    dispatchLoading(true);
    return OrderCartService.createOrderCart({
      productId: product.id!,
      reference: question.reference || (!!unionOptionsConsentTimestamp ? 'Gewerkschaftsmitglied' : undefined),
      affiliatePartnerId: appStore.ref || localStorage.getItem('ref') || undefined, // use "ref" item from local storage if available. Is set thru GTM to have an affiliate tracking for returning customers without branding the flow
      referenceLabelType: !!unionOptionsConsentTimestamp ? ReferenceLabelTypeEnum.VOUCHER : appStore.referenceLabelType,
      survey: {
        apartmentSize: Number(question.apartmentSize),
        receptionDate: question.deliverAt,
        hasLegalInsurance: Boolean(question.hasLegalInsurance),
        householdMembers: Number(question.household),
        landlord: undefined,
        legalInsurance: legalInsurance ?? undefined,
        periodMonthFrom: question.periodMonthFrom,
        periodMonthTo: question.periodMonthTo,
        periodYearFrom: question.periodYearFrom,
        periodYearTo: question.periodYearTo,
        postalCode: question.postcode,
        rent: Number(question.rentalFee),
        businessType: question.businessType,
        balance:
          question.paymentType === PaymentTypeEnum.ADDITIONAL_PAYMENT
            ? -Math.abs(Number(question.paymentAmount))
            : Math.abs(Number(question.paymentAmount)),
        integratedPartnerId: integratedPartnerId ?? undefined,
      },
      billingConsentTimestamp: billingConsentTimestamp ?? null,
      unionOptionsConsentTimestamp: unionOptionsConsentTimestamp ?? null,
      variant: abTestVariant,
      state: getState(appStore, {
        partnerRedirectBaseUrl: config?.ENV.PARTNER_REDIRECT_BASE_URL,
      }),
    })
      .then((orderCart) => {
        const checkoutUrl = `${process.env.REACT_APP_CHECKOUT_URL!}?orderCartId=${orderCart.id}`;
        const loginUrlWithTransitParams = getLoginUrlWithTransitParams({
          ...transitUrlParams,
          requestedUri: checkoutUrl.toString(),
        });

        dispatchEventToParentWindow('ready_for_checkout', {
          orderCartId: orderCart.id,
          checkoutUrl: loginUrlWithTransitParams.toString(),
        });
        persistTransitParams();
        window.location.assign(checkoutUrl.toString());
      })
      .finally(() => dispatchLoading(false));
  };

  return { createOrderCart };
}
